import {
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptRelationship,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  DomainsAndRanges,
  addDomainsAndRanges,
  addDomainsAndRangesFromHierarchy,
  isValidMappingDomainAndRange,
} from 'src/containers/ProjectDiagram/utils/isValidDomainAndRange';
import {
  getReasonsForInvalidClassMapping,
  getReasonsForInvalidRelationshipMapping,
  isValidClassMapping,
  isValidRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';

export const getValidMappingsByResource = (model: Model | ModelSnapshot) => {
  const validClassMappingsByResource: Record<string, Mapping[]> = {};
  const validClassMappingKeys = new Set<string>();
  const validRelationshipMappingsByResource: Record<
    string,
    RelationshipMapping[]
  > = {};

  // need to iterate over class mappings first, since relationship mapping validity
  // is dependent on class mappings
  const classMappings: Mapping[] = [];
  const relationshipMappings: RelationshipMapping[] = [];

  const classMappingIriByKey: Map<string, string> = new Map();
  const domainsAndRanges: DomainsAndRanges = new Map();

  model.linkDataArray.forEach((linkData) => {
    if (isMapping(linkData)) {
      classMappingIriByKey.set(
        linkData.key,
        getModelConceptClassValue(linkData.to || '')
      );
      classMappings.push(linkData);
    } else if (isRelationshipMapping(linkData)) {
      relationshipMappings.push(linkData);
    } else if (isModelConceptRelationship(linkData)) {
      addDomainsAndRanges(domainsAndRanges)(linkData);
    }
  });

  addDomainsAndRangesFromHierarchy(model, domainsAndRanges);

  classMappings.forEach((classMapping) => {
    if (!isValidClassMapping(classMapping)) {
      return;
    }

    const resourceId = getResourceValue(classMapping.from);

    if (!validClassMappingsByResource[resourceId]) {
      validClassMappingsByResource[resourceId] = [];
    }
    validClassMappingsByResource[resourceId].push(classMapping);
    validClassMappingKeys.add(classMapping.key);
  });

  relationshipMappings.forEach((relationshipMapping) => {
    const classMapping = classMappings.find(
      (mapping) =>
        mapping.key === (relationshipMapping.from || relationshipMapping.to)
    );
    if (
      !classMapping ||
      !isValidRelationshipMapping(relationshipMapping, classMapping)
    ) {
      return;
    }

    const isValidSelection =
      (relationshipMapping.from &&
        validClassMappingKeys.has(relationshipMapping.from)) ||
      (relationshipMapping.to &&
        validClassMappingKeys.has(relationshipMapping.to));
    if (!isValidSelection) {
      return;
    }

    if (
      !isValidMappingDomainAndRange(
        relationshipMapping,
        classMappingIriByKey,
        domainsAndRanges
      )
    ) {
      console.warn(
        `Skipping export for invalid relationship mapping (id: ${relationshipMapping.value})`
      );
      return;
    }

    const resourceId = getResourceValue(classMapping.from);
    if (!validRelationshipMappingsByResource[resourceId]) {
      validRelationshipMappingsByResource[resourceId] = [];
    }
    validRelationshipMappingsByResource[resourceId].push(relationshipMapping);
  });

  return {
    classMappingsByResource: validClassMappingsByResource,
    relationshipMappingsByResource: validRelationshipMappingsByResource,
  };
};

export const getInvalidMappingsByResource = (model: Model | ModelSnapshot) => {
  const validClassMappings: Mapping[] = [];
  const invalidMappingsByResource: Record<
    string,
    Array<Mapping & { relationshipIri?: string }>
  > = {};
  model.linkDataArray.forEach((data) => {
    if (!isMapping(data)) {
      return;
    }

    const maybeReasons = getReasonsForInvalidClassMapping(data);
    if (!maybeReasons) {
      validClassMappings.push(data);
      return;
    }
    const { reasons, quiet } = maybeReasons;
    reasons.forEach((reason) => console.warn(reason));

    if (!quiet) {
      const resourceId = getResourceValue(data.from);
      if (!invalidMappingsByResource[resourceId]) {
        invalidMappingsByResource[resourceId] = [];
      }
      invalidMappingsByResource[resourceId].push(data);
    }
  });

  model.linkDataArray.forEach((data) => {
    if (!isRelationshipMapping(data)) {
      return;
    }

    const classMapping = validClassMappings.find(
      (mapping) => mapping.key === (data.from || data.to)
    );

    const maybeReasons = getReasonsForInvalidRelationshipMapping(
      data,
      classMapping
    );
    if (!maybeReasons) {
      return;
    }
    const { reasons, quiet } = maybeReasons;
    reasons.forEach((reason) => console.warn(reason));

    if (!quiet && classMapping) {
      const resourceId = getResourceValue(classMapping.from);
      if (!invalidMappingsByResource[resourceId]) {
        invalidMappingsByResource[resourceId] = [];
      }
      invalidMappingsByResource[resourceId].push({
        ...classMapping,
        relationshipIri: data.relationshipIri,
      });
    }
  });

  return invalidMappingsByResource;
};
